/*@flow*/
import isObject from './isObject';
import isArray from './isArray';
import hasOwn from './hasOwn';

function getShallowCopy<T: any>(x: T): T {
  if (isObject(x)) {
    if (x instanceof Date || x instanceof RegExp) {
      return x;
    }
    return { ...x };
  }
  if (isArray(x)) {
    return x.concat();
  }
  return x;
}

function isCommand(k: string) {
  return hasOwn.call(update.commands, k);
}

function getCommand(k: string) {
  return update.commands[k];
}

// built-in commands

function $apply(f: Function, value: any): any {
  return f(value);
}

function $push<T: any>(elements: T[], arr: T[]): T[] {
  if (elements.length > 0) {
    return arr.concat(elements);
  }
  return arr;
}

function $remove(keys: any[], obj: {}): {...} {
  if (keys.length > 0) {
    obj = getShallowCopy(obj);
    keys.forEach(k => {
      delete obj[k];
    });
  }
  return obj;
}

function $set(value: any): any {
  return value;
}

type Index = string | number;

function $splice(splices: Array<Array<Index>>, arr: Array<Index>): Array<Index> {
  if (splices.length > 0) {
    arr = getShallowCopy(arr);
    return splices.reduce((acc, splice) => {
      acc.splice.apply(acc, splice);
      return acc;
    }, arr);
  }
  return arr;
}

function $swap(config: { from: number, to: number }, arr: any[]): Array<any> {
  if (config.from !== config.to) {
    arr = getShallowCopy(arr);
    let element = arr[config.to];
    arr[config.to] = arr[config.from];
    arr[config.from] = element;
  }
  return arr;
}

function $unshift<T: any>(elements: T[], arr: T[]): T[] {
  if (elements.length > 0) {
    return elements.concat(arr);
  }
  return arr;
}

function $merge(whatToMerge: {}, value: {}): {...} {
  let isChanged = false;
  let result = getShallowCopy(value);
  Object.keys(whatToMerge).forEach(k => {
    result[k] = whatToMerge[k];
    isChanged = isChanged || result[k] !== value[k];
  });
  return isChanged ? result : value;
}

function update<T: any>(instance: T, patch: {}): T {
  let value = instance;
  let isChanged = false;
  let newValue;
  Object.keys(patch).forEach(k => {
    if (isCommand(k)) {
      newValue = getCommand(k)(patch[k], value);
      if (newValue !== instance) {
        isChanged = true;
        value = newValue;
      } else {
        value = instance;
      }
    } else {
      if (value === instance) {
        value = getShallowCopy(instance);
      }
      newValue = update(value[k], patch[k]);
      isChanged = isChanged || newValue !== value[k];
      value[k] = newValue;
    }
  });
  return isChanged ? value : instance;
}

update.commands = {
  $apply,
  $push,
  $remove,
  $set,
  $splice,
  $swap,
  $unshift,
  $merge,
};

export default update;
